import { Dialog } from '@angular/cdk/dialog';
import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { GuildEntity, GuildMemberEntity, HeroEntity, UserEntity } from '@generated/gql';
import { getHeroExperiencePercent, getHeroVirtualLevel, getNetworkTokenIcon } from '@helpers/data-helper';
import { Formatter } from '@helpers/formatter';
import { AttributesModel } from '@models/attributes.model';
import { HeroMetadataType } from '@models/graph/hero-metadata-type';
import { TranslatePipe } from '@ngx-translate/core';
import { ApiService } from '@services/api.service';
import { AppStateService } from '@services/app-state-service/app-state.service';
import { HeaderStateModel } from '@services/app-state-service/models/header-state.model';
import { DestroyService } from '@services/destroy.service';
import { GuildSubgraphService } from '@services/graph/guild-subgraph.service';
import { HeroSubgraphService } from '@services/graph/hero-subgraph.service';
import { SubgraphService } from '@services/graph/subgraph.service';
import { UserSubgraphService } from '@services/graph/user-subgraph.service';
import { Mediator } from '@services/mediator.service';
import { GuildsControllerService } from '@services/onchain/guilds-controller.service';
import { MultiCallService } from '@services/onchain/multi-call.service';
import { TokenService } from '@services/onchain/token.service';
import { ProviderService } from '@services/provider.service';
import { RevenueCatService } from '@services/revenue-cat.service';
import { SoundService } from '@services/sound.service';
import { StorageService } from '@services/storage.service';
import { Web3authService } from '@services/web3auth.service';
import { AchievementsActions } from '@shared/actions/achievement.actions';
import { HeaderActions } from '@shared/actions/header.actions';
import { HeroActions } from '@shared/actions/hero.actions';
import { MenuActions } from '@shared/actions/menu.actions';
import { TutorialActions } from '@shared/actions/tutorial.actions';
import { AccountDialogComponent } from '@shared/components/account-dialog/account-dialog.component';
import { AchievementsService } from '@shared/components/achievements/achievements.service';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { BalanceComponent } from '@shared/components/balance/balance.component';
import { BarStatComponent } from '@shared/components/bar-stat/bar-stat.component';
import { BottlesDialogComponent } from '@shared/components/bottles-dialog/bottles-dialog.component';
import { GuildAvatarComponent } from '@shared/components/guild-avatar/guild-avatar.component';
import { InventoryComponent } from '@shared/components/hero-inventory/inventory/inventory.component';
import { HeroLevelUpComponent } from '@shared/components/hero-level-up/hero-level-up.component';
import { HeroStatItemInputInterface } from '@shared/components/hero-stat-item/hero-stat-item-input-interface';
import { InfoDialogComponent } from '@shared/components/info-dialog/info-dialog.component';
import { SettingsDialogComponent } from '@shared/components/settings-dialog/settings-dialog.component';
import { SwapCoinsDialogComponent } from '@shared/components/swap-coins-dialog/swap-coins-dialog.component';
import { GET_CORE_ADDRESSES, GET_TOKEN_INFO } from '@shared/constants/addresses/addresses.constant';
import {
  ATTRIBUTE_TO_DESCRIPTION,
  ATTRIBUTE_TO_ICON,
  ATTRIBUTE_TO_NAME,
  ATTRIBUTES,
} from '@shared/constants/attributes.constant';
import { CHAINS } from '@shared/constants/chain-ids.constant';
import { CHECKBOX_STATE } from '@shared/constants/checkbox-states.constant';
import { GuildRequestStatus } from '@shared/constants/game.constant';
import { HERO_CORE_STATS_META } from '@shared/constants/hero-levels.constant';
import { MAIN_ROUTES } from '@shared/constants/routes.constant';
import { SACRA_GAME_SELECTED_HERO } from '@shared/constants/storage-keys.constant';
import { TUTORIALS } from '@shared/constants/tutorial.constant';
import { getCurrentWeek } from '@shared/time-utils';
import { getValueByKey } from '@shared/utils';
import { ethers, formatUnits } from 'ethers';
import { NGXLogger } from 'ngx-logger';
import { debounceTime, filter, finalize, map, Observable, of, shareReplay, switchMap, takeUntil, tap } from 'rxjs';

import { getAvatarByHero } from '../../constants/heroes.constant';

import { ROUTE_WITH_EXCLUDED_BARS } from './constants/header-new.constant';

// 5 min
const UPDATE_PVP_MS = 60_000 * 5;

@Component({
  selector: 'app-header-new',
  standalone: true,
  templateUrl: './header-new.component.html',
  styleUrls: ['./header-new.component.scss'],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, BarStatComponent, BalanceComponent, TranslatePipe, GuildAvatarComponent, RouterLink],
})
export class HeaderNewComponent implements OnInit, OnChanges {
  @Input() menuState = { isOpen: false };
  @Input() providerInited = false;

  account: string;
  chainId: number;

  userName?: string;
  userAvatar?: string;
  virtualLevel = '0';
  accountFormatted: string = '';
  // hero: HeroEntity | undefined;
  user?: UserEntity;
  networkTokenBalance: string;
  networkTokenBalanceFormatted = 0;
  gameTokenBalance = '0';
  magicTokenBalance = '0';
  strengthToken = '0';
  dexterityToken = '0';
  isShowBars = true;
  headerState: HeaderStateModel | undefined;
  avatarUrl: string;
  heroAttributes: AttributesModel;
  coreStatInfo: HeroStatItemInputInterface[] = [];
  additionalStatInfo: HeroStatItemInputInterface[] = [];
  isBuying = false;
  isAbleToClaimRewards = false;
  isNeedRepair = false;
  private _isAbleLevelUp = false;

  get isAbleLevelUp(): boolean {
    return this._isAbleLevelUp;
  }

  set isAbleLevelUp(value: boolean) {
    const stackTrace = new Error().stack;
    this.logger.trace('SETTER isAbleLevelUp called with', value, 'old value:', this._isAbleLevelUp);
    this.logger.trace('SETTER stack trace:', stackTrace?.split('\n').slice(1, 4).join('\n'));
    this._isAbleLevelUp = value;
  }
  iconName: string = 'matic';
  isTokensTheSame = true;
  isHasTutorial = false;
  isHideMenuButton = false;
  magicTokenAdr: string;
  isAbleToPvP = false;
  guildMember?: GuildMemberEntity;
  guild = signal<GuildEntity | null>(null);

  ATTRIBUTES = ATTRIBUTES;
  MAIN_ROUTES = MAIN_ROUTES;

  tutorialsKeys = Object.keys(TUTORIALS);
  foundTutorialKey?: string;
  isHideMenuButtons = true;

  guildRequests$: Observable<number> = of(0);

  protected readonly heroes = signal<HeroEntity[]>([]);
  protected readonly selectedHero = signal<HeroEntity | null>(null);
  private readonly destroyRef = inject(DestroyRef);
  private readonly localStorage = inject(StorageService);
  private readonly guildSubgraphService = inject(GuildSubgraphService);
  private readonly userSubgraphService = inject(UserSubgraphService);
  private readonly heroSubgraphService = inject(HeroSubgraphService);
  private readonly revenueCatService = inject(RevenueCatService);

  protected readonly CHECKBOX_STATE = CHECKBOX_STATE;

  constructor(
    private router: Router,
    private providerService: ProviderService,
    private web3authService: Web3authService,
    private changeDetectorRef: ChangeDetectorRef,
    private appStateService: AppStateService,
    private tokenService: TokenService,
    private subgraphService: SubgraphService,
    private multiCallService: MultiCallService,
    private destroy$: DestroyService,
    private logger: NGXLogger,
    private location: Location,
    private dialog: Dialog,
    private soundService: SoundService,
    private apiService: ApiService,
    private mediator: Mediator,
    private storageService: StorageService,
    private achievementsService: AchievementsService,
    private guildsControllerService: GuildsControllerService,
  ) {
    this.magicTokenAdr = GET_CORE_ADDRESSES(Number(environment['CHAIN_ID'])).magicToken;
  }

  ngOnInit(): void {
    this.checkBars(this.router.url);
    this.checkTutorial(this.router.url);

    let url = this.router.url;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(
        debounceTime(300),
        tap(event => {
          url = (event as NavigationEnd).url;
        }),
        switchMap(() => this.loadHeroData()),
      )
      .subscribe(() => {
        this.checkBars(url);
        this.checkTutorial(url);
      });

    this.mediator.ofAction(HeroActions.UpdateName, this.destroy$).subscribe(() => this.loadUserData());
    this.mediator.ofAction(MenuActions.MenuClose, this.destroy$).subscribe(() => this.closeMenu());
    this.mediator.ofAction(HeroActions.HeroChoose, this.destroy$).subscribe(() => this.selectHero());

    this.appStateService
      .getHeaderStateObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.headerState = state;

        this.changeDetectorRef.detectChanges();
      });

    this.providerService.transactionEnded
      .pipe(filter(data => data.isNeedUpdateHero === true || data.isNeedUpdateBalances === true))
      .subscribe(data => {
        this.logger.trace('UPDATE HEADER INFO', data);
        if (data.isNeedUpdateHero) {
          this.loadHeroData();
        }

        if (data.isNeedUpdateBalances) {
          this.loadBalances();
        }
      });

    this.providerService.subscribeOnAccountAndNetwork(
      this.destroy$,
      this.changeDetectorRef,
      (account: string) => {
        this.account = account;
        this.init();
        this.initCheckPvpLogic();
      },
      (chainId: number) => {
        this.chainId = chainId;

        const core = GET_CORE_ADDRESSES(this.chainId);
        const gameToken = core.gameToken;
        const magicToken = core.magicToken;
        const strengthToken = core.strengthToken;
        const dexterityToken = core.dexterityToken;
        this.isTokensTheSame = magicToken === gameToken && strengthToken === gameToken && dexterityToken === gameToken;

        if (CHAINS.has(this.chainId)) {
          this.iconName = getNetworkTokenIcon();
        }

        this.init();
      },
    );

    this.appStateService
      .getSelectedHero()
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => {
        if (id) {
          this.checkSelectedHero(id);
        }
      });
  }

  ngOnChanges({ providerInited }: SimpleChanges): void {
    if (providerInited && providerInited.currentValue !== undefined) {
      this.checkBars(this.router.url);
    }
  }

  init() {
    if (this.account && this.chainId) {
      this.logger.trace('START HEADER INIT');
      this.isAbleToClaimRewards = false;
      this.loadHeroes();
      this.loadBalances();
      this.loadUserData();
      this.getFormattedAccount();
      this.getGuildRequests();
      this.getGuild();
    }
  }

  getFormattedAccount() {
    const userInfo = this.web3authService.getUserInfo();
    if (userInfo) {
      this.accountFormatted = userInfo.email.substring(0, 9) + '...';
    } else {
      this.accountFormatted = Formatter.formatAddress(this.account, 'short');
    }
    this.changeDetectorRef.detectChanges();
  }

  loadHeroData() {
    if (this.selectedHero()) {
      this.logger.info('LOADING hero inf for header');
      this.subgraphService
        .hero$(this.selectedHero()!.meta.id, this.selectedHero()!.heroId)
        .pipe(shareReplay(1), takeUntil(this.destroy$))
        .subscribe(hero => {
          this.logger.trace('Hero data:', {
            experience: hero.stats.experience,
            level: hero.stats.level,
            previousLevelExperienceRequire: hero.previousLevelExperienceRequire,
            nextLevelExperienceRequire: hero.nextLevelExperienceRequire,
          });
          this.selectedHero.set(hero as HeroEntity);
          this.virtualLevel = getHeroVirtualLevel(
            this.selectedHero()!.stats.experience,
            this.selectedHero()!.stats.level,
          ).toFixed();
          this.showNewAchievements(this.selectedHero()!);

          this.isNeedRepair = hero.items.filter(i => i.durability / i.meta.durability <= 0.2).length > 0;
          const expPerc = Math.min(
            getHeroExperiencePercent(
              hero.stats.experience,
              hero.previousLevelExperienceRequire,
              hero.nextLevelExperienceRequire,
            ),
            100,
          );
          const ableToLevelUp = expPerc >= 100 && hero.stats.level < 100;
          this.logger.trace('BEFORE SET isAbleLevelUp', this.isAbleLevelUp, 'new value:', ableToLevelUp);
          // Only update if the new value is true, or if current value is false
          // This prevents overwriting true with false from subsequent emissions
          if (ableToLevelUp || !this.isAbleLevelUp) {
            this.isAbleLevelUp = ableToLevelUp;
          } else {
            this.logger.trace('SKIPPED SET isAbleLevelUp - keeping true value');
          }
          this.logger.trace('AFTER SET isAbleLevelUp', this.isAbleLevelUp);
          this.logger.trace('this.isNeedRepair', this.isNeedRepair);
          this.logger.trace('this.isAbleLevelUp value', ableToLevelUp);
          this.logger.trace('expPerc', expPerc);
          this.logger.trace('hero.stats.level', hero.stats.level);

          this.loadStats();
          this.checkBars(this.router.url);
          this.loadAbleToClaimRewards();

          this.avatarUrl = getAvatarByHero(this.selectedHero()!);

          this.logger.trace('BEFORE detectChanges isAbleLevelUp', this.isAbleLevelUp);
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
          this.logger.trace('AFTER detectChanges isAbleLevelUp', this.isAbleLevelUp);

          // Double check after a small delay
          setTimeout(() => {
            this.logger.trace('AFTER setTimeout isAbleLevelUp', this.isAbleLevelUp);
            this.changeDetectorRef.detectChanges();
          }, 100);
        });
    } else {
      this.logger.info('Hero token or id is empty');
    }

    return of(void 0);
  }

  initCheckPvpLogic() {
    if (this.account) {
      this.guildSubgraphService
        .guildMemberShortInfo$(this.account.toLowerCase())
        .pipe(takeUntil(this.destroy$))
        .subscribe(member => {
          this.guildMember = member;
          if (this.guildMember) {
            this.checkAvailablePvp();
            // check available pvp every 1 minutes
            setInterval(() => {
              this.checkAvailablePvp();
            }, UPDATE_PVP_MS);
          }
        });
    }
  }

  checkAvailablePvp() {
    if (this.guildMember) {
      const currentWeek = getCurrentWeek();
      this.subgraphService
        .allPvpHeroByWeekAndBiome$(currentWeek + '', this.guildMember.guild.activePvp?.biome || 0)
        .pipe(
          filter(pvpHeroes => pvpHeroes && pvpHeroes.length > 0),
          filter(() => !!this.guildMember),
          takeUntil(this.destroy$),
        )
        .subscribe(pvpHeroes => {
          const filteredPvpHeroes = pvpHeroes.filter(pvpHero => {
            if (pvpHero.pvpGuild.guild.id === this.guildMember!.guild.id) {
              return false;
            }
            return (
              pvpHero.fightsA.filter(f => !f.completed).length === 0 &&
              pvpHero.fightsB.filter(f => !f.completed).length === 0
            );
          });
          this.isAbleToPvP = filteredPvpHeroes.length > 0;
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  private loadUserData() {
    if (this.account) {
      this.userSubgraphService
        .userGeneralInfo$(this.account.toLowerCase())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(user => {
          if (user) {
            this.user = user as UserEntity;
            this.userName = user.name;
            this.userAvatar = user.uri;
          }
        });
    }
  }

  loadBalances() {
    if (!this.account || this.account === '0' || this.account === '') {
      return;
    }
    const core = GET_CORE_ADDRESSES(this.chainId);

    const calls = [
      this.multiCallService.getEthBalance(this.chainId, this.account),
      this.tokenService.balanceOf(core.gameToken, this.account),
      this.tokenService.balanceOf(core.magicToken, this.account),
      // this.tokenService.balanceOf(core.strengthToken, this.account),
      // this.tokenService.balanceOf(core.dexterityToken, this.account),
    ];
    this.multiCallService
      .aggregate$(calls, this.chainId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        const [nativeBalance, gameTokenBalance, magicToken] = result.returnData;

        this.tokenService.tokenBalanceCache.set(core.gameToken, BigInt(gameTokenBalance));
        this.tokenService.tokenBalanceCache.set(core.magicToken, BigInt(magicToken));
        // this.tokenService.tokenBalanceCache.set(core.strengthToken, BigInt(strengthToken));
        // this.tokenService.tokenBalanceCache.set(core.dexterityToken, BigInt(dexterityToken));
        this.tokenService.tokenBalanceCache.set(core.networkToken, BigInt(nativeBalance));

        this.networkTokenBalance = Formatter.formatCurrency(+(+formatUnits(nativeBalance)).toFixed(2));
        this.networkTokenBalanceFormatted = +(+formatUnits(nativeBalance)).toFixed(2);
        this.gameTokenBalance = (+formatUnits(gameTokenBalance)).toFixed(2);
        this.magicTokenBalance = Formatter.formatCurrency(
          +formatUnits(magicToken, GET_TOKEN_INFO(this.chainId, core.magicToken).decimals),
        );
        // this.strengthToken = Formatter.formatCurrency(
        //   +formatUnits(strengthToken, GET_TOKEN_INFO(this.chainId, core.strengthToken).decimals),
        // );
        // this.dexterityToken = Formatter.formatCurrency(
        //   +formatUnits(dexterityToken, GET_TOKEN_INFO(this.chainId, core.dexterityToken).decimals),
        // );

        // if (+ethers.formatUnits(nativeBalance) === 0) {
        //   // for some reason this message sometimes do not compile
        //   // hope this delay will help
        //   setTimeout(() => this.showNotEnoughTokensInfo(), 1000);
        // }

        // this.showSpeedUpDialog();

        this.changeDetectorRef.markForCheck();

        if (this.chainId === -1) {
          // some testnet with refuel mechanic
          this.logger.trace('Check need refuel', +formatUnits(nativeBalance), +formatUnits(gameTokenBalance));
          if (+formatUnits(nativeBalance) < 0.1 || +formatUnits(gameTokenBalance) < 1) {
            this.apiService
              .refuel(this.account)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                this.loadBalances();
              });
          }
        }
      });
  }

  // showNotEnoughTokensInfo() {
  //   if (this.notEnoughTokensInfoShowed) {
  //     return;
  //   }
  //   this.notEnoughTokensInfoShowed = true;
  //   const chain = CHAINS.get(this.chainId) as CHAIN_TYPE;
  //   this.dialog.open(InfoDialogComponent, {
  //     panelClass: 'app-overlay-pane',
  //     disableClose: true,
  //     data: {
  //       infoTitle: 'Not enough network coins!',
  //       infoDesc: this.translateService.instant(
  //         chain[CHAIN_FIELDS.IS_TEST_NET] ? 'block-app-buy-coins.test-net' : 'block-app-buy-coins.main-net',
  //         {
  //           net: chain[CHAIN_FIELDS.NAME],
  //           faucetLink: chain[CHAIN_FIELDS.FAUCET_LINK],
  //         },
  //       ),
  //       canClose: true,
  //     },
  //   });
  // }

  // showSpeedUpDialog() {
  //   if (this.isPrivateKeyImported || !this.web3authService.getUserInfo() || this.speedUpDialogOpened) {
  //     return;
  //   }
  //
  //   this.speedUpDialogOpened = true;
  //
  //   this.dialog.open(StorePrivateKeyDialogComponent, {
  //     panelClass: 'app-overlay-pane',
  //     disableClose: true,
  //     data: {},
  //   });
  // }

  showNewAchievements(hero: HeroEntity) {
    if (!hero || !this.account || hero.owner.id.toLowerCase() !== this.account.toLowerCase()) {
      return;
    }

    const newAchievements = this.achievementsService.getNewAchievements(this.storageService, hero);

    // only 1 achievement currently
    setTimeout(() => {
      this.mediator.dispatch(new AchievementsActions.Show(newAchievements[0]));
    }, 1000);
  }

  openAccountModal() {
    this.dialog.open(AccountDialogComponent, {
      panelClass: 'app-overlay-pane',
      data: {
        account: this.account,
        chainId: this.chainId,
        userName: this.userName,
        userAvatar: this.userAvatar,
        gamePoints: this.user?.gamePoints || '0',
      },
    });
  }

  goBack() {
    if (!this.headerState?.backUrl) {
      this.location.back();
    } else {
      this.router.navigate(this.headerState?.backUrl);
    }

    this.mediator.dispatch(new HeaderActions.GoBack());
  }

  handleMenu() {
    this.menuState.isOpen = !this.menuState.isOpen;

    this.getGuildRequests();
  }

  closeMenu() {
    this.menuState.isOpen = false;
  }

  openHome() {
    this.router.navigate([MAIN_ROUTES.MAIN]);

    this.closeMenu();
  }

  openSettings() {
    this.dialog.open(SettingsDialogComponent, {
      panelClass: 'app-overlay-pane',
    });

    this.closeMenu();
  }

  openInventory() {
    if (this.selectedHero()) {
      this.soundService.play({ key: 'inventory' });

      const dialog = this.dialog.open(InventoryComponent, {
        panelClass: 'app-overlay-pane',
        data: { heroToken: this.selectedHero()!.meta.id, heroId: this.selectedHero()!.heroId },
      });

      dialog.closed
        .pipe(
          filter(isNeedToUpdate => isNeedToUpdate === true),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.mediator.dispatch(new HeroActions.Update());
        });
    }
  }

  openTopList() {
    this.router.navigate([MAIN_ROUTES.TOP_LIST]);

    this.closeMenu();
  }

  openLeaderboard() {
    this.router.navigate([MAIN_ROUTES.LEADERBOARD]);

    this.closeMenu();
  }

  openTransform() {
    if (this.selectedHero()) {
      this.router.navigate([
        MAIN_ROUTES.MAIN,
        MAIN_ROUTES.TRANSFORM,
        this.selectedHero()?.meta.id,
        this.selectedHero()?.heroId,
      ]);
    } else {
      this.router.navigate([MAIN_ROUTES.CREATE]);
    }

    this.closeMenu();
  }

  goToMap() {
    this.soundService.play({ key: 'go_to_map' });

    this.router.navigate([
      MAIN_ROUTES.MAIN,
      MAIN_ROUTES.MAP,
      this.selectedHero()?.meta.id,
      this.selectedHero()?.heroId,
    ]);

    this.closeMenu();
  }

  createHero() {
    this.router.navigate([MAIN_ROUTES.CREATE]);

    this.closeMenu();
  }

  openCamp() {
    if (!this.selectedHero()) {
      return;
    }

    this.router.navigate([MAIN_ROUTES.MAIN, MAIN_ROUTES.HERO_CAMP]);
  }

  openBottlesDialog(shouldCheckItems = true) {
    if (this.selectedHero() && this.user) {
      this.dialog.open(BottlesDialogComponent, {
        panelClass: 'app-overlay-pane',
        data: {
          chainId: this.chainId,
          account: this.account,
          heroToken: this.selectedHero()?.meta.id,
          heroTokenId: this.selectedHero()?.heroId,
          hero: this.selectedHero(),
        },
      });
    }
  }

  openTutorial() {
    if (this.foundTutorialKey) {
      this.mediator.dispatch(
        new TutorialActions.ToggleTutorial(true, TUTORIALS[this.foundTutorialKey], true, this.foundTutorialKey!),
      );
    }
  }

  heroExpPerc() {
    return Math.min(
      getHeroExperiencePercent(
        this.selectedHero() ? this.selectedHero()!.stats.experience : 0,
        this.selectedHero() ? this.selectedHero()!.previousLevelExperienceRequire : 0,
        this.selectedHero() ? this.selectedHero()!.nextLevelExperienceRequire : 0,
      ),
      100,
    );
  }

  expBarWidth() {
    return Math.min(this.heroExpPerc(), 100) / 100;
  }

  loadAbleToClaimRewards() {
    // if (!this.user) {
    //   return;
    // }
    //
    // this.isAbleToClaimRewards = false;
    // this.user.heroes.map(hero => {
    //   if (hero.reinforcementRewardTokens.length > 0 || hero.reinforcementRewardItems.length > 0) {
    //     this.isAbleToClaimRewards = true;
    //     this.changeDetectorRef.detectChanges();
    //   }
    // });

    if (this.selectedHero()) {
      if (
        this.selectedHero()!.reinforcementRewardTokens.length > 0 ||
        this.selectedHero()!.reinforcementRewardItems.length > 0
      ) {
        this.isAbleToClaimRewards = true;
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  buyTokens(tokenName: string) {
    if (this.isBuying) {
      return;
    }

    let tokenAdr;

    switch (tokenName) {
      case 'sacra':
        tokenAdr = GET_CORE_ADDRESSES(this.chainId).gameToken;
        break;
      case 'usdc':
        tokenAdr = GET_CORE_ADDRESSES(this.chainId).gameToken;
        break;
      case 'matic':
        tokenAdr = GET_CORE_ADDRESSES(this.chainId).gameToken;
        break;
      case 'tetu':
        tokenAdr = GET_CORE_ADDRESSES(this.chainId).gameToken;
        break;
      default:
        throw new Error('wrong token');
    }

    // if (!isSimpleTestNetwork(this.chainId)) {
    //   this.tokenService.openBuyTokenExternalLink(tokenAdr, this.chainId);
    //   return;
    // }

    this.isBuying = true;
    this.changeDetectorRef.detectChanges();

    this.tokenService
      .buyTokens$(this.account, '', tokenAdr, ethers.parseUnits('100'), this.chainId)
      .pipe(
        finalize(() => {
          this.isBuying = false;
          this.changeDetectorRef.detectChanges();
        }),
        filter(() => !!this.selectedHero()),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.updateFeeBalance(this.selectedHero()!.meta);
      });
  }

  updateFeeBalance(hero: HeroMetadataType) {
    this.tokenService
      .balanceOf$(hero.feeToken.token.id, this.account)
      .pipe(takeUntil(this.destroy$))
      .subscribe(gameTokenBalance => {
        this.gameTokenBalance = Formatter.formatCurrency(+ethers.formatUnits(gameTokenBalance));

        this.changeDetectorRef.detectChanges();
      });
  }

  onShowLevelUpDialog() {
    this.dialog.open(HeroLevelUpComponent, {
      panelClass: 'app-overlay-pane',
      data: { heroToken: this.selectedHero()?.meta.id, heroId: this.selectedHero()?.heroId },
    });
  }

  onShowReinforcementDialog() {
    this.router.navigate([MAIN_ROUTES.REINFORCEMENT, this.selectedHero()?.meta.id, this.selectedHero()?.heroId]);
  }

  onShowRepairDialog() {
    this.openInventory();
  }

  isShowThunderbolt() {
    return this.providerService.isLocalPK();
  }

  onSessionSettings() {
    if (this.web3authService.getUserInfo()) {
      this.router.navigate([MAIN_ROUTES.WEB3AUTH_SETTINGS]);
    } else {
      this.router.navigate([MAIN_ROUTES.DELEGATE]);
    }
  }

  onShowPvPDialog() {
    this.dialog.open(InfoDialogComponent, {
      panelClass: 'app-overlay-pane',
      data: {
        infoTitle: 'PvP Availability',
        infoDesc:
          `<div class="g-flex g-flex--align-center gap-20">` +
          `<img class="account__wallet-icon" width="80" height="80" src="assets/images/ui/icons/other/pvp-points.png" />` +
          `<div class="text-center">Enemy's hero is waiting for PvP fight! Check your guild.</div>` +
          `</div>`,
        customWidth: 700,
      },
    });
  }

  private checkBars(url: string) {
    const cleanUrl = url.indexOf('?') >= 0 ? url.substring(0, url.indexOf('?')) : url;

    this.isHideMenuButtons =
      cleanUrl === '/' ||
      cleanUrl === `/${MAIN_ROUTES.TOP_LIST}` ||
      cleanUrl === `/${MAIN_ROUTES.CHAR}` ||
      cleanUrl === `/${MAIN_ROUTES.ALL_ITEMS}` ||
      cleanUrl === `/${MAIN_ROUTES.ACCOUNT}` ||
      cleanUrl === `/${MAIN_ROUTES.EMAIL}`;

    this.isHideMenuButton = cleanUrl === '/' || (!this.providerInited && cleanUrl === `/${MAIN_ROUTES.TOP_LIST}`);

    this.isShowBars = !ROUTE_WITH_EXCLUDED_BARS.some(it => cleanUrl.includes(`/${it}/`) || cleanUrl.includes(`/${it}`));

    this.changeDetectorRef.detectChanges();
  }

  private checkTutorial(url: string) {
    this.foundTutorialKey = this.tutorialsKeys.find(it => url.indexOf(it) >= 0);

    // if (this.foundTutorialKey) {
    //   this.isHasTutorial = true;
    //
    //   this.changeDetectorRef.detectChanges();
    // }
  }

  isSonicChain(): boolean {
    return Number(environment['CHAIN_ID']) === 146;
  }

  swapCoins() {
    this.dialog.open(SwapCoinsDialogComponent, {
      panelClass: 'app-overlay-pane',
      data: {},
    });
  }

  private loadStats() {
    this.heroAttributes = AttributesModel.fromStringArray(this.selectedHero() ? this.selectedHero()!.attributes : []);

    this.coreStatInfo = HERO_CORE_STATS_META.map((stat): HeroStatItemInputInterface => {
      return {
        ...stat,
        text: getValueByKey<AttributesModel>(this.heroAttributes, stat.id),
        attributeIndex: stat.index ?? 0,
      };
    });

    this.additionalStatInfo = [
      {
        id: 'lightningResistance',
        name: ATTRIBUTE_TO_NAME.get(ATTRIBUTES.LIGHTNING_RESISTANCE) ?? '',
        icon: ATTRIBUTE_TO_ICON.get(ATTRIBUTES.LIGHTNING_RESISTANCE) ?? '',
        text: this.heroAttributes.lightningResistance,
        description: ATTRIBUTE_TO_DESCRIPTION.get(ATTRIBUTES.LIGHTNING_RESISTANCE) ?? '',
        attributeIndex: ATTRIBUTES.LIGHTNING_RESISTANCE,
      },
      {
        id: 'fireResistance',
        name: ATTRIBUTE_TO_NAME.get(ATTRIBUTES.FIRE_RESISTANCE) ?? '',
        icon: ATTRIBUTE_TO_ICON.get(ATTRIBUTES.FIRE_RESISTANCE) ?? '',
        text: this.heroAttributes.fireResistance,
        description: ATTRIBUTE_TO_DESCRIPTION.get(ATTRIBUTES.FIRE_RESISTANCE) ?? '',
        attributeIndex: ATTRIBUTES.FIRE_RESISTANCE,
      },
      {
        id: 'coldResistance',
        name: ATTRIBUTE_TO_NAME.get(ATTRIBUTES.COLD_RESISTANCE) ?? '',
        icon: ATTRIBUTE_TO_ICON.get(ATTRIBUTES.COLD_RESISTANCE) ?? '',
        text: this.heroAttributes.coldResistance,
        description: ATTRIBUTE_TO_DESCRIPTION.get(ATTRIBUTES.COLD_RESISTANCE) ?? '',
        attributeIndex: ATTRIBUTES.COLD_RESISTANCE,
      },
    ];
  }

  private getGuildRequests() {
    this.guildRequests$ = this.guildsControllerService.memberOf$(this.account, this.chainId).pipe(
      filter(memberOfGuildId => memberOfGuildId > 0),
      switchMap(memberOfGuildId => this.subgraphService.allGuildRequestsByGuild$(memberOfGuildId + '')),
      map(requests => requests.filter(it => it.status === GuildRequestStatus.NONE).length || 0),
      takeUntil(this.destroy$),
    );
  }

  private getGuild() {
    this.guildsControllerService
      .memberOf$(this.account, this.chainId)
      .pipe(
        filter(memberOfGuildId => memberOfGuildId > 0),
        switchMap(memberOfGuildId => this.guildSubgraphService.guildShortInfo$(memberOfGuildId!.toString())),
        takeUntil(this.destroy$),
      )
      .subscribe(guild => {
        this.guild.set(guild);
      });
  }

  private loadHeroes() {
    this.heroes.set([]);

    this.heroSubgraphService
      .fetchHeroesByUserAndLive$(this.account)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(heroes => {
        this.heroes.set(heroes);

        if (heroes.length > 0) {
          this.selectHero();
        }
      });
  }

  private checkSelectedHero(id: string) {
    if (this.localStorage.get(SACRA_GAME_SELECTED_HERO)) {
      const key = JSON.parse(this.localStorage.get(SACRA_GAME_SELECTED_HERO));
      if (`${this.account}-${this.chainId}-${this.selectedHero()?.id}` !== key) {
        this.loadHeroInfo(id);
      }
    }
  }

  private selectHero() {
    if (this.localStorage.get(SACRA_GAME_SELECTED_HERO)) {
      const key = JSON.parse(this.localStorage.get(SACRA_GAME_SELECTED_HERO));

      const found = this.heroes().find(hero => `${this.account}-${this.chainId}-${hero.id}` === key);

      if (found) {
        this.selectedHero.set(found);
      } else {
        this.loadHeroInfo(this.heroes()[0].id);
      }
    } else {
      this.loadHeroInfo(this.heroes()[0].id);
    }
  }

  private loadHeroInfo(id: string): void {
    this.heroSubgraphService
      .hero$(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(hero => {
        this.selectedHero.set(hero as HeroEntity);
        this.loadHeroData();
        this.changeDetectorRef.detectChanges();
      });
  }

  protected readonly Formatter = Formatter;

  getIsAbleLevelUp(): boolean {
    const value = this._isAbleLevelUp;
    this.logger.trace('getIsAbleLevelUp() called, returning', value);
    this.logger.trace('getIsAbleLevelUp() _isAbleLevelUp:', this._isAbleLevelUp);
    this.logger.trace('getIsAbleLevelUp() getter returns:', this.isAbleLevelUp);
    return value;
  }
}
